import ConditionMatcher from "./ConditionMatcher"
import Evaluator, { EvaluatorContext, EvaluatorRequest } from "../evalautor/Evaluator"
import { DecisionReason, Experiment, TargetCondition } from "../../model/model"
import ValueOperatorMatcher from "./ValueOperatorMatcher"
import ExperimentEvaluation from "../evalautor/experiment/ExperimentEvaluation"
import ObjectUtil from "../../util/ObjectUtil"
import TypeUtil from "../../util/TypeUtil"
import ExperimentContextualEvaluator from "../evalautor/experiment/ExperimentContextualEvaluator"

export default class ExperimentConditionMatcher implements ConditionMatcher {
  private readonly abTestMatcher: AbTestConditionMatcher
  private readonly featureFlagMatcher: FeatureFlagConditionMatcher

  constructor(abTestMatcher: AbTestConditionMatcher, featureFlagMatcher: FeatureFlagConditionMatcher) {
    this.abTestMatcher = abTestMatcher
    this.featureFlagMatcher = featureFlagMatcher
  }

  /**
   * @override
   */
  matches(request: EvaluatorRequest, context: EvaluatorContext, condition: TargetCondition): boolean {
    switch (condition.key.type) {
      case "AB_TEST":
        return this.abTestMatcher.matches(request, context, condition)
      case "FEATURE_FLAG":
        return this.featureFlagMatcher.matches(request, context, condition)
      default:
        throw new Error(`Unsupported TargetKeyType [${condition.key.type}]`)
    }
  }
}

abstract class ExperimentMatcher extends ExperimentContextualEvaluator {
  protected readonly valueOperatorMatcher: ValueOperatorMatcher

  protected constructor(evaluator: Evaluator, valueOperatorMatcher: ValueOperatorMatcher) {
    super(evaluator)
    this.valueOperatorMatcher = valueOperatorMatcher
  }

  matches(request: EvaluatorRequest, context: EvaluatorContext, condition: TargetCondition): boolean {
    const key = ObjectUtil.requiredNotNullOrUndefined(
      TypeUtil.asNumber(condition.key.name),
      () => `Invalid key [${condition.key.type}, ${condition.key.name}]`
    )

    const experiment = this.experiment(request, key)
    if (ObjectUtil.isNullOrUndefined(experiment)) {
      return false
    }

    const evaluation = this.evaluate(request, context, experiment)
    return this.evaluationMatches(evaluation as ExperimentEvaluation, condition)
  }

  abstract experiment(request: EvaluatorRequest, key: number): Experiment | undefined

  abstract evaluationMatches(evaluation: ExperimentEvaluation, condition: TargetCondition): boolean
}

export class AbTestConditionMatcher extends ExperimentMatcher {
  constructor(evaluator: Evaluator, valueOperatorMatcher: ValueOperatorMatcher) {
    super(evaluator, valueOperatorMatcher)
  }

  private static AB_TEST_MATCHED_REASONS = [
    DecisionReason.OVERRIDDEN,
    DecisionReason.TRAFFIC_ALLOCATED,
    DecisionReason.EXPERIMENT_COMPLETED,
    DecisionReason.TRAFFIC_ALLOCATED_BY_TARGETING
  ]

  /**
   * @override
   */
  experiment(request: EvaluatorRequest, key: number): Experiment | undefined {
    return request.workspace.getExperimentOrNull(key)
  }

  /**
   * @override
   */
  resolve(
    request: EvaluatorRequest,
    context: EvaluatorContext,
    evaluation: ExperimentEvaluation
  ): ExperimentEvaluation {
    if (evaluation.reason === DecisionReason.TRAFFIC_ALLOCATED) {
      return evaluation.with(DecisionReason.TRAFFIC_ALLOCATED_BY_TARGETING)
    }
    return evaluation
  }

  /**
   * @override
   */
  evaluationMatches(evaluation: ExperimentEvaluation, condition: TargetCondition): boolean {
    if (!AbTestConditionMatcher.AB_TEST_MATCHED_REASONS.includes(evaluation.reason)) {
      return false
    }
    return this.valueOperatorMatcher.matches(evaluation.variationKey, condition.match)
  }
}

export class FeatureFlagConditionMatcher extends ExperimentMatcher {
  constructor(evaluator: Evaluator, valueOperatorMatcher: ValueOperatorMatcher) {
    super(evaluator, valueOperatorMatcher)
  }

  /**
   * @override
   */
  experiment(request: EvaluatorRequest, key: number): Experiment | undefined {
    return request.workspace.getFeatureFlagOrNull(key)
  }

  /**
   * @override
   */
  resolve(
    request: EvaluatorRequest,
    context: EvaluatorContext,
    evaluation: ExperimentEvaluation
  ): ExperimentEvaluation {
    return evaluation
  }

  /**
   * @override
   */
  evaluationMatches(evaluation: ExperimentEvaluation, condition: TargetCondition): boolean {
    const on = evaluation.variationKey !== "A"
    return this.valueOperatorMatcher.matches(on, condition.match)
  }
}
